/**
 * Booking requests repository.
 *
 * Owns `booking_requests` — public booking form submissions on the
 * `/booking/:orgId` page (no auth required to insert). BookingPage.tsx
 * already validates the form fields client-side via `bookingSchema`
 * (react-hook-form + zodResolver), but this call site is reachable by
 * anyone who talks to the Supabase REST API directly, bypassing the
 * React form entirely — so the insert payload is re-validated here too
 * (CLAUDE.md §2.4: keine unvalidierte Nutzereingabe landet in DB-Queries).
 */

import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { APPOINTMENT_TYPES, PREFERRED_TIMES } from '@/schemas/booking';

const CreateBookingRequestSchema = z.object({
  organization_id: z.string().uuid(),
  name: z.string().trim().min(2).max(100),
  email: z.string().trim().email(),
  phone: z.string().trim().max(30).nullable(),
  preferred_date: z.string().nullable(),
  preferred_time: z.enum(PREFERRED_TIMES),
  appointment_type: z.enum(APPOINTMENT_TYPES),
  message: z.string().trim().max(1000).nullable(),
});

export type CreateBookingRequestInput = z.infer<typeof CreateBookingRequestSchema>;

export async function createBookingRequest(input: CreateBookingRequestInput) {
  const parsed = CreateBookingRequestSchema.safeParse(input);
  if (!parsed.success) {
    throw new Error(`Ungültige Buchungsanfrage: ${parsed.error.message}`);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.from('booking_requests') as any).insert([parsed.data]);
  if (error) throw error;
}
